<template>
  <div class="ol-root">

    <!-- Top bar -->
    <header class="ol-header">
      <!-- Logo -->
      <div class="ol-header-logo">
        <RouterLink to="/" class="ol-logo">
          <img src="/new-logo.png" class="ol-logo-mark" alt="">
          <span class="ol-logo-text">Maths Eleven Plus</span>
        </RouterLink>
      </div>

      <!-- Step pills -->
      <div class="ol-steps">
        <div
          v-for="n in totalSteps"
          :key="n"
          class="ol-step-pill"
          :class="{
            'ol-step-active': n === displayStep,
            'ol-step-done': n < displayStep,
            'ol-step-future': n > displayStep,
          }"
        >
          <span v-if="n < currentStep" class="ol-step-check">✓</span>
          {{ stepLabels[n - 1] }}
        </div>
      </div>

      <div class="ol-header-spacer"></div>
    </header>

    <!-- Main -->
    <main class="ol-main">
      <!-- Step dots -->
      <div class="ol-dots">
        <div
          v-for="n in totalSteps"
          :key="n"
          class="ol-dot"
          :class="{
            'ol-dot-active': n === displayStep,
            'ol-dot-done': n < displayStep,
          }"
        ></div>
        <span class="ol-dots-label">Step {{ displayStep }} of {{ totalSteps }}</span>
      </div>

      <div class="ol-content">
        <RouterView />
      </div>
    </main>

  </div>
</template>

<script setup>
import { provide, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';

const route = useRoute();
const userStore = useUserStore();

const currentStep = ref(1);

const isUpgrade    = computed(() => route.query.upgrade    === 'true');
const isSkipSkills = computed(() => route.query.skipSkills === 'true');
const isPaidPlan   = computed(() => {
  const plan = userStore.session?.effectivePlan || userStore.session?.plan || 'free';
  return plan !== 'free';
});

// Pro→Family upgrade: 1 step (Welcome only)
// Free→Pro upgrade:   1 step (Skills Check only)
// Free new signup / downgrade: 1 step (Child Setup only)
// Paid new signup: 2 steps (Child Setup → Skills Check)
const totalSteps = computed(() => {
  if (isUpgrade.value) return 1;
  if (!isPaidPlan.value) return 1;
  return 2;
});

const stepLabels = computed(() => {
  if (isUpgrade.value && isSkipSkills.value) return ['Welcome'];
  if (isUpgrade.value) return ['Skills Check'];
  if (!isPaidPlan.value) return ['Child Setup'];
  return ['Child Setup', 'Skills Check'];
});

const displayStep = computed(() => Math.min(currentStep.value, totalSteps.value));

provide('onboardingStep', currentStep);
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=Jost:wght@300;400;500;600;700&display=swap');

.ol-root {
  min-height: 100vh;
  background: #0D1628;
  font-family: 'Jost', sans-serif;
  background-image:
    radial-gradient(circle at 80% 20%, rgba(184,144,42,0.06) 0%, transparent 50%),
    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23B8902A' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E");
  display: flex;
  flex-direction: column;
}

/* Header */
.ol-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  padding: 0 32px;
  height: 60px;
  background: rgba(13,22,40,0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(184,144,42,0.15);
}

.ol-header-logo { flex: 1; }
.ol-header-spacer { flex: 1; }

.ol-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}
.ol-logo-mark {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
}
.ol-logo-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 17px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.02em;
}

.ol-steps {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ol-step-pill {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  white-space: nowrap;
  transition: all 0.2s;
}
.ol-step-active {
  background: #B8902A;
  color: #0D1628;
}
.ol-step-done {
  background: rgba(184,144,42,0.12);
  color: rgba(255,255,255,0.7);
  border: 1px solid rgba(184,144,42,0.2);
}
.ol-step-future {
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.3);
  border: 1px solid rgba(255,255,255,0.06);
}
.ol-step-check { font-size: 10px; }

/* Main */
.ol-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 24px 48px;
}

.ol-dots {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
  margin-top: 8px;
}
.ol-dot {
  width: 8px;
  height: 8px;
  border-radius: 100px;
  background: rgba(255,255,255,0.2);
  transition: all 0.3s;
}
.ol-dot-active {
  width: 28px;
  background: #B8902A;
}
.ol-dot-done {
  background: rgba(184,144,42,0.5);
}
.ol-dots-label {
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,0.5);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.ol-content {
  width: 100%;
  max-width: 540px;
}

@media (max-width: 600px) {
  .ol-header { padding: 0 16px; }
  .ol-step-pill { font-size: 10px; padding: 4px 10px; }
  .ol-logo-text { display: none; }
}
</style>
